import { AxiosPromise } from 'axios';
import { apiService, apiUrl, HEADER_AUTH_TOKEN } from '../apiService';
import PageableType from '../utils/pageableType';
import { PinDto } from '../../features/users/components/VerificationPopover';
import {
  FundraiserDetailsDto,
  FundraiserFilterDto,
  FundraiserPageUpdateDto,
  NewTechnicalUserDto,
  PhoneDto,
  UserDetailsDto,
  UserDto,
  UserFilterDto,
  UserPublicDto,
  UserUpdateDto,
} from './userService.dto';

class UserService {
  static BASE_URL = `${apiUrl}/api/user`;

  fetchUserCurrent(): AxiosPromise<UserDto> {
    const url = `${UserService.BASE_URL}/users/current`;
    return apiService.get(url);
  }

  fetchPublicUsers(userIds: number[]): AxiosPromise<UserPublicDto[]> {
    const url = `${UserService.BASE_URL}/public/users/bulk`;
    return apiService.get(url, { ids: userIds.join(',') });
  }

  fetchPublicUser(id: number): AxiosPromise<UserPublicDto> {
    const url = `${UserService.BASE_URL}/public/users/${id}`;
    return apiService.get(url);
  }

  fetchBlockadeReasons(): AxiosPromise<{ name: string }[]> {
    const url = `${UserService.BASE_URL}/admin/blockade-reasons`;
    return apiService.get(url);
  }

  fetchUsers(filter: UserFilterDto, blocked?: boolean): AxiosPromise<PageableType<UserDetailsDto>> {
    const url = `${UserService.BASE_URL}/admin/users`;
    return apiService.get(url, { ...filter, blocked });
  }

  createTechnicalUser(data: NewTechnicalUserDto): AxiosPromise<UserDetailsDto> {
    const url = `${UserService.BASE_URL}/admin/technical-users`;
    return apiService.post(url, {}, {}, data);
  }

  updateUser(data: UserUpdateDto, authToken?: string) {
    const url = `${UserService.BASE_URL}/users`;
    return apiService.put(url, {}, { [HEADER_AUTH_TOKEN]: authToken }, data);
  }

  fetchSingleUser(id: number): Promise<UserDetailsDto> {
    const url = `${UserService.BASE_URL}/admin/users`;
    return apiService.get(url, { page: 0, size: 1, id: id }).then(response => {
      return response?.data?.content[0];
    });
  }

  fetchFundraisers(filter: FundraiserFilterDto): AxiosPromise<PageableType<FundraiserDetailsDto>> {
    const url = `${UserService.BASE_URL}/admin/fundraisers`;
    return apiService.get(url, { ...filter });
  }

  fetchSingleFundraiser(id: number): Promise<FundraiserDetailsDto> {
    const url = `${UserService.BASE_URL}/admin/fundraisers`;
    return apiService.get(url, { page: 0, size: 1, id: id }).then(response => {
      return response?.data?.content[0];
    });
  }

  verifyFundraiser(userId: number) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/fundraiser-verification`;
    return apiService.post(url);
  }

  blockUser(userId: number, reason: string, comment?: string) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/blockade`;
    return apiService.post(url, {}, {}, { comment, reason });
  }

  unblockUser(userId: number) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/blockade`;
    return apiService.delete(url);
  }

  closeAccount(userId: number) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}`;
    return apiService.delete(url);
  }

  cleanupAccount(userId: number, firstName: string, lastName: string) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/content`;
    return apiService.delete(url, {}, {}, { firstName, lastName });
  }

  fetchPhoneActivationPin(userId: number): AxiosPromise<PinDto> {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/phone-activation-pin`;
    return apiService.get(url);
  }

  confirmSignup(userId: number) {
    const url = `${UserService.BASE_URL}/admin/signup-confirmation`;
    return apiService.post(url, {}, {}, { userId });
  }

  updateFundraiserPage(userId: number, fundraiserPageUpdateDto: FundraiserPageUpdateDto) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/fundraiser-page`;
    return apiService.put(url, {}, {}, fundraiserPageUpdateDto);
  }

  deleteFundraiserPage(userId: number) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/fundraiser-page`;
    return apiService.delete(url);
  }

  updatePhoneNumber(userId: number, phoneDto: PhoneDto): AxiosPromise<PhoneDto> {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/phone`;
    return apiService.put(url, {}, {}, phoneDto);
  }

  updateEmail(userId: number, email: string): AxiosPromise<string> {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/email`;
    return apiService.put(url, {}, {}, { email });
  }

  confirmEmail(userId: number) {
    const url = `${UserService.BASE_URL}/admin/users/${userId}/confirm-email`;
    return apiService.post(url);
  }
}

const userService = new UserService();

export { userService, UserService };
